import { getSubdomain } from '../utils/subdomain';
import { apiActivity } from './apiActivity';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // A UX hint only — it lets the API tell us when someone signed in at the
    // wrong subdomain. Never trusted as an authorization input: the server
    // resolves the real tenant from the authenticated user row.
    const workspace = getSubdomain();
    if (workspace) {
      config.headers['X-Tenant-Slug'] = workspace;
    }

    apiActivity.start();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for global error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    apiActivity.done();
    return response;
  },
  async (error) => {
    apiActivity.done();
    const originalRequest = error.config;

    // A suspended or cancelled workspace is not an auth failure — clearing the
    // token and bouncing to /login would let the user sign straight back in and
    // hit the same wall, with nothing explaining why.
    if (error.response?.status === 403 && /workspace/i.test(error.response?.data?.message || '')) {
      error.message = error.response.data.message;
      return Promise.reject(error);
    }

    // Handle 401 (Unauthorized) errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Clear stored token and redirect to login.
      // NOTE: despite the _retry flag there is no refresh-and-retry here, so
      // an expired access token is a hard logout even when a valid refresh
      // token exists. Pre-existing; orthogonal to tenancy.
      localStorage.removeItem('authToken');
      window.location.href = '/login';

      return Promise.reject(error);
    }

    // Handle other errors - extract user-friendly message from API response
    if (error.response?.data) {
      const data = error.response.data;
      if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        // Validation errors (422) - show the first field-level error
        error.message = data.errors.map((e: any) => e.msg || e.message).join('. ');
      } else if (data.message) {
        error.message = data.message;
      }
    } else if (!error.response) {
      error.message = 'Network error. Please check your connection.';
    }

    return Promise.reject(error);
  }
);

// Envelope contract: every ERP API response is either
//   { status: 'success', message?, data: { <namedKey>: ... } }
// or, for endpoints with nothing to return (e.g. some deletes),
//   { status: 'success', message }               (no `data` key at all).
// unwrapEnvelope returns response.data.data when both `status` and `data`
// are present, otherwise it passes the raw response.data through unchanged.
// All five verbs must share this exact logic so a delete/put/patch never
// silently reverts to returning the whole envelope.
function unwrapEnvelope<T>(response: AxiosResponse): T {
  if (response.data && response.data.status && response.data.data) {
    return response.data.data;
  }
  return response.data;
}

// Generic API methods
export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.get(url, config).then((response) => unwrapEnvelope<T>(response)),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.post(url, data, config).then((response) => unwrapEnvelope<T>(response)),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.put(url, data, config).then((response) => unwrapEnvelope<T>(response)),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.patch(url, data, config).then((response) => unwrapEnvelope<T>(response)),

  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.delete(url, config).then((response) => unwrapEnvelope<T>(response)),
};

export default apiClient;